import React, { useState, useEffect } from 'react';
import { queries as queriesApi } from '../../api/client';
import { Search, Plus, RefreshCw, CheckCircle2, XCircle, Loader2, ChevronDown } from 'lucide-react';

const Badge = ({ children, variant = "default" }) => {
  const variants = {
    default: "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300",
    success: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400",
    warning: "bg-amber-50 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400",
    danger: "bg-rose-50 text-rose-700 dark:bg-rose-900/50 dark:text-rose-400",
    high: "bg-rose-50 text-rose-700",
    medium: "bg-amber-50 text-amber-700",
    low: "bg-stone-100 text-stone-600",
  };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${variants[variant]}`}>{children}</span>;
};

export default function QueriesPage({ activeBrandId }) {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newQueries, setNewQueries] = useState('');
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState('');

  const loadQueries = async () => {
    if (!activeBrandId) return;
    setLoading(true);
    try {
      const params = filter ? { priority: filter } : {};
      const data = await queriesApi.list(activeBrandId, params);
      setQueries(data.queries || []);
    } catch (e) {
      console.error('Failed to load queries:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadQueries(); }, [activeBrandId, filter]);

  const handleAddQueries = async () => {
    if (!newQueries.trim() || !activeBrandId) return;
    setAdding(true);
    try {
      const queryList = newQueries.split('\n').map(q => q.trim()).filter(Boolean);
      await queriesApi.create(activeBrandId, queryList);
      setNewQueries('');
      setShowAdd(false);
      loadQueries();
    } catch (e) {
      console.error('Failed to add queries:', e);
    } finally {
      setAdding(false);
    }
  };

  const llmNames = { CHATGPT: 'GPT', PERPLEXITY: 'PPX', CLAUDE: 'CLD', GEMINI: 'GEM', GOOGLE_AIO: 'GAI' };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="text-xs font-medium tracking-widest uppercase block mb-2 text-stone-500">Tracking</span>
          <h1 className="font-serif text-2xl md:text-3xl text-stone-900 dark:text-white">Queries</h1>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-700 dark:text-stone-300">
            <option value="">All priorities</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>
          <button onClick={loadQueries} className="px-3 py-1.5 text-sm border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 flex items-center gap-1 text-stone-600">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => setShowAdd(true)} className="px-4 py-1.5 text-sm bg-stone-900 text-white dark:bg-white dark:text-stone-900 flex items-center gap-1">
            <Plus className="w-4 h-4" /> Add Queries
          </button>
        </div>
      </div>

      {/* Add Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAdd(false)} />
          <div className="relative bg-white dark:bg-stone-900 w-full max-w-lg border border-stone-200 dark:border-stone-700 shadow-xl p-6">
            <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-4">Add Queries</h2>
            <p className="text-sm text-stone-500 mb-3">Enter one query per line</p>
            <textarea value={newQueries} onChange={(e) => setNewQueries(e.target.value)}
              className="w-full h-40 px-4 py-3 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900"
              placeholder="best crm for startups\nmarketing automation tools\n..." />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm border border-stone-200 dark:border-stone-700 text-stone-600">Cancel</button>
              <button onClick={handleAddQueries} disabled={adding} className="px-4 py-2 text-sm bg-stone-900 text-white dark:bg-white dark:text-stone-900 flex items-center gap-1">
                {adding && <Loader2 className="w-4 h-4 animate-spin" />} Add
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>
      ) : (
        <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-stone-50 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-700 text-xs font-medium text-stone-500 uppercase tracking-wider">
            <div className="col-span-4">Query</div>
            <div className="col-span-1 text-center">Priority</div>
            <div className="col-span-1 text-center">GPT</div>
            <div className="col-span-1 text-center">PPX</div>
            <div className="col-span-1 text-center">CLD</div>
            <div className="col-span-1 text-center">GEM</div>
            <div className="col-span-1 text-center">GAI</div>
            <div className="col-span-1 text-center">Cited</div>
            <div className="col-span-1 text-center">Trend</div>
          </div>

          {queries.length === 0 ? (
            <div className="p-8 text-center text-stone-500">No queries tracked yet. Add some queries to get started.</div>
          ) : (
            queries.map((q) => (
              <div key={q.query_id} className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-stone-100 dark:border-stone-700 last:border-0 hover:bg-stone-50 dark:hover:bg-stone-800/50 items-center">
                <div className="col-span-4">
                  <p className="font-medium text-stone-900 dark:text-white text-sm truncate">{q.query}</p>
                  {q.search_volume && <p className="text-xs text-stone-500">{q.search_volume?.toLocaleString()} vol</p>}
                </div>
                <div className="col-span-1 text-center">
                  <Badge variant={q.priority?.toLowerCase() || 'default'}>{q.priority}</Badge>
                </div>
                {['CHATGPT', 'PERPLEXITY', 'CLAUDE', 'GEMINI', 'GOOGLE_AIO'].map(llm => {
                  const result = q.results?.[llm];
                  return (
                    <div key={llm} className="col-span-1 text-center">
                      {result?.is_cited ? (
                        <span className="text-emerald-600 font-medium text-sm">#{result.position}</span>
                      ) : result ? (
                        <XCircle className="w-4 h-4 text-stone-300 mx-auto" />
                      ) : (
                        <span className="text-stone-300">—</span>
                      )}
                    </div>
                  );
                })}
                <div className="col-span-1 text-center">
                  <span className={`text-sm font-medium ${q.cited_count > 0 ? 'text-emerald-600' : 'text-stone-400'}`}>
                    {q.cited_count}/5
                  </span>
                </div>
                <div className="col-span-1 text-center">
                  <Badge variant={q.trend === 'UP' ? 'success' : q.trend === 'DOWN' ? 'danger' : 'default'}>
                    {q.trend || 'STABLE'}
                  </Badge>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
